/**
 * Contracts for module "deliverables". Owner: delivery.
 * Content studio asset library, deliverables (state machine, versions, attachments, client decisions),
 * alerts, remediation tasks from audit findings and branding orders.
 */
import { z } from 'zod';

// ---------- asset library (QM-A08-001) ----------
export const ASSET_KINDS = ['image', 'video', 'document'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/** Allowed MIME types per kind and the per-kind size limit (bytes). */
export const ASSET_POLICY: Record<AssetKind, { mimes: readonly string[]; maxBytes: number }> = {
  image: { mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'], maxBytes: 25 * 1024 * 1024 },
  video: { mimes: ['video/mp4', 'video/quicktime', 'video/webm'], maxBytes: 2 * 1024 * 1024 * 1024 },
  document: { mimes: ['application/pdf'], maxBytes: 50 * 1024 * 1024 },
};

export const TagSchema = z.string().trim().min(1).max(40).regex(/^[\p{L}\p{N} _-]+$/u);

export const CreateAssetSchema = z.object({
  title: z.string().trim().min(1).max(200),
  filename: z.string().trim().min(1).max(255),
  mime: z.string().trim().min(3).max(100),
  sizeBytes: z.number().int().positive(),
  tags: z.array(TagSchema).max(30).default([]),
});
export type CreateAsset = z.infer<typeof CreateAssetSchema>;

export const NewAssetVersionSchema = CreateAssetSchema.pick({ filename: true, mime: true, sizeBytes: true });
export const UpdateAssetSchema = z.object({ title: z.string().trim().min(1).max(200).optional(), tags: z.array(TagSchema).max(30).optional() });

export const AssetStatusSchema = z.enum(['awaiting_upload', 'scanning', 'available', 'rejected']);
export type AssetStatus = z.infer<typeof AssetStatusSchema>;

// ---------- deliverables (QM-B05-002, QM-NC08-001, QM-B06-001) ----------
export const DELIVERABLE_STATES = [
  'draft', 'internal_review', 'client_review', 'changes_requested', 'approved', 'rejected', 'published', 'cancelled',
] as const;
export type DeliverableState = (typeof DELIVERABLE_STATES)[number];

/**
 * The deliverable state machine. draft -> review -> approved -> published, with internal and client review
 * split (B05-001 markers), change requests, rejection, approval revocation and cancellation.
 */
export const DELIVERABLE_TRANSITIONS: Record<DeliverableState, readonly DeliverableState[]> = {
  draft: ['internal_review', 'cancelled'],
  internal_review: ['client_review', 'draft', 'cancelled'],
  client_review: ['approved', 'changes_requested', 'rejected', 'cancelled'],
  changes_requested: ['draft', 'internal_review', 'cancelled'],
  approved: ['published', 'client_review'],
  rejected: ['draft', 'cancelled'],
  published: [],
  cancelled: [],
};
export const canTransition = (from: DeliverableState, to: DeliverableState) => DELIVERABLE_TRANSITIONS[from].includes(to);

/** States a person moves to by hand; client decisions and publishing have their own routes. */
export const MANUAL_TARGETS: readonly DeliverableState[] = ['draft', 'internal_review', 'client_review', 'cancelled'];

export const AttachmentInputSchema = z.union([
  z.object({ kind: z.literal('asset'), assetId: z.string().min(1).max(64), label: z.string().max(200).optional() }),
  z.object({ kind: z.literal('link'), url: z.string().url().max(2000).refine((u) => /^https:\/\//i.test(u), 'https only'), label: z.string().max(200).optional() }),
]);
export type AttachmentInput = z.infer<typeof AttachmentInputSchema>;

export const CreateDeliverableSchema = z.object({
  title: z.string().trim().min(1).max(200),
  kind: z.string().trim().min(1).max(40).default('content'),
  channel: z.string().trim().max(40).optional(),
  ownerUserId: z.string().min(1).max(64),
  approverUserId: z.string().min(1).max(64).optional(),
  dueAt: z.string().datetime({ offset: true }).optional(),
  clientLabel: z.string().trim().max(120).optional(),
  note: z.string().max(2000).optional(),
  attachments: z.array(AttachmentInputSchema).max(20).default([]),
});
export type CreateDeliverable = z.infer<typeof CreateDeliverableSchema>;

export const UpdateDeliverableSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  ownerUserId: z.string().min(1).max(64).optional(),
  approverUserId: z.string().min(1).max(64).nullable().optional(),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export const NewDeliverableVersionSchema = z.object({
  note: z.string().max(2000).optional(),
  attachments: z.array(AttachmentInputSchema).min(1).max(20),
});

export const TransitionSchema = z.object({
  to: z.enum(DELIVERABLE_STATES),
  reason: z.string().max(1000).optional(),
});

export const DecisionSchema = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('approve'), comment: z.string().max(2000).optional() }),
  z.object({ decision: z.literal('reject'), comment: z.string().trim().min(1).max(2000) }),
  z.object({ decision: z.literal('request_changes'), comment: z.string().trim().min(1).max(2000) }),
]);
export type Decision = z.infer<typeof DecisionSchema>;

// ---------- alerts (QM-B05-003, QM-B06-003) ----------
export const ALERT_KINDS = ['due_soon', 'overdue', 'approval_reminder', 'approval_escalation'] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

/** Queue message sent to MESSAGING_QUEUE for every alert (consumer: notifications). */
export interface DeliverableAlertMessage {
  type: 'deliverables.alert';
  tenantId: string;
  idempotencyKey: string;
  payload: { alertId: string; kind: AlertKind; deliverableId: string; recipients: string[]; messageKey: string; dueAt: string | null };
}

// ---------- remediation tasks (QM-A11-004) ----------
export const FindingSeveritySchema = z.enum(['critical', 'high', 'medium', 'low']);
export const FindingInputSchema = z.object({
  findingId: z.string().min(1).max(100),
  title: z.string().trim().min(1).max(300),
  severity: FindingSeveritySchema,
  findingUrl: z.string().max(2000).optional(),
  evidenceRef: z.string().max(2000).optional(),
});
export const CreateTasksFromFindingsSchema = z.object({
  auditId: z.string().min(1).max(100),
  findings: z.array(FindingInputSchema).min(1).max(200),
  ownerUserId: z.string().min(1).max(64).optional(),
  /** default due offsets by severity are applied when omitted */
  dueAt: z.string().datetime({ offset: true }).optional(),
});
export const UpdateTaskSchema = z.object({
  status: z.enum(['open', 'in_progress', 'done']).optional(),
  ownerUserId: z.string().min(1).max(64).nullable().optional(),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export const RescanResultSchema = z.object({
  auditId: z.string().min(1).max(100),
  findingId: z.string().min(1).max(100),
  passed: z.boolean(),
  evidenceRef: z.string().min(1).max(2000),
  scannedAt: z.string().datetime({ offset: true }),
});
export const InternalRescanSchema = RescanResultSchema.extend({ tenantId: z.string().min(1).max(64) });

// ---------- branding orders (QM-A10-002) ----------
export const BRANDING_PACKAGES = {
  logo_guidelines: {
    priceMinor: 750000, currency: 'AED',
    deliverables: [
      { title: 'Logo concepts', kind: 'logo', owner: 'team:design', dueDays: 7 },
      { title: 'Final logo files', kind: 'logo', owner: 'team:design', dueDays: 14 },
      { title: 'Brand guidelines', kind: 'guidelines', owner: 'team:design', dueDays: 21 },
    ],
  },
  website: {
    priceMinor: 1800000, currency: 'AED',
    deliverables: [
      { title: 'Sitemap and wireframes', kind: 'website', owner: 'team:web', dueDays: 10 },
      { title: 'Visual design', kind: 'website', owner: 'team:design', dueDays: 20 },
      { title: 'Website build and launch', kind: 'website', owner: 'team:web', dueDays: 35 },
    ],
  },
  collateral: {
    priceMinor: 450000, currency: 'AED',
    deliverables: [
      { title: 'Business cards and letterhead', kind: 'collateral', owner: 'team:design', dueDays: 7 },
      { title: 'Company profile', kind: 'collateral', owner: 'team:design', dueDays: 14 },
    ],
  },
  uniforms: {
    priceMinor: 300000, currency: 'AED',
    deliverables: [{ title: 'Uniform designs', kind: 'uniforms', owner: 'team:design', dueDays: 14 }],
  },
} as const;
export type BrandingPackageCode = keyof typeof BRANDING_PACKAGES;
export const CreateBrandingOrderSchema = z.object({
  packageCode: z.enum(Object.keys(BRANDING_PACKAGES) as [BrandingPackageCode, ...BrandingPackageCode[]]),
});
export const BrandingPaymentCallbackSchema = z.object({
  tenantId: z.string().min(1).max(64),
  orderId: z.string().min(1).max(64),
});

// ---------- inbound events from other modules (qm-jobs -> POST /v1/deliverables/internal/events) ----------
/** MESSAGING_QUEUE `visibility.task.approved` (analytics, QM-A03-006): an approved visibility task becomes a remediation deliverable. */
export const VisibilityTaskApprovedEventSchema = z.object({
  type: z.literal('visibility.task.approved'),
  tenantId: z.string().min(1).max(64),
  idempotencyKey: z.string().min(1).max(200),
  payload: z.object({ taskId: z.string().min(1).max(64), kind: z.string().min(1).max(60), title: z.string().min(1).max(200), approvedBy: z.string().min(1).max(64) }),
});
/** Tools the deliverables module executes when an approval routed with sourceModule "deliverables" is executed. */
export const DELIVERABLE_APPROVAL_TOOLS = ['deliver_deliverable', 'create_calendar_tasks'] as const;
/** Owner placeholder for deliverables created by the system (reassign in the calendar). */
export const SYSTEM_OWNER = 'team:marketing';

/**
 * SYSTEM_QUEUE `delivery.customer_fact.changed`: a Customer 360 fact (QM-C02-001) keyed by the tenant, since delivery
 * does not know CRM customer ids. The crm consumer maps tenantId -> crm_customers.external_tenant_ref and upserts
 * the fact (kind deliverable | report) through its facts path.
 */
export const DELIVERY_FACT_EVENT = 'delivery.customer_fact.changed';
export const DeliveryFactSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('deliverable'), ref: z.string(), title: z.string().max(200), status: z.string().max(40), progressPct: z.number().int().min(0).max(100), dueAt: z.string().nullable() }),
  z.object({ kind: z.literal('report'), period: z.string().regex(/^\d{4}-\d{2}$/), status: z.string().max(40), reportRef: z.string().nullable() }),
]);
export type DeliveryFact = z.infer<typeof DeliveryFactSchema>;
export const DeliveryFactEventSchema = z.object({
  type: z.literal(DELIVERY_FACT_EVENT), tenantId: z.string(), idempotencyKey: z.string(), payload: z.object({ externalTenantRef: z.string(), fact: DeliveryFactSchema }),
});

/** Progress shown in Customer 360 per deliverable state. */
export const DELIVERABLE_PROGRESS: Record<DeliverableState, number> = {
  draft: 10, internal_review: 40, client_review: 60, changes_requested: 50, approved: 90, rejected: 0, published: 100, cancelled: 0,
};
